import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Download, Eye, Trash2 } from 'lucide-react';
import { Document, Page } from '@/Components/shared/react-pdf-lazy';

interface FileInputProps {
  value: File | null;
  onChange: (file: File | null) => void;
  label?: string;
  accept: string;
  maxSize: number; // en Mo
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
  showPreview?: boolean;
  showActions?: boolean;
  description?: string;
  existingFileUrl?: string;
  existingFileName?: string;
}

/**
 * Composant FileInput enrichi pour les formulaires
 * Supporte drag & drop, preview visuel (images/PDF), et actions (Voir/Télécharger/Supprimer)
 */
export const FileInput: React.FC<FileInputProps> = ({
  value,
  onChange,
  label,
  accept,
  maxSize,
  required = false,
  error,
  disabled = false,
  className = '',
  dataTestId,
  showPreview = true,
  showActions = true,
  description,
  existingFileUrl,
  existingFileName = 'Document actuel',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'pdf' | 'other'>('other');
  const [pdfError, setPdfError] = useState(false);
  const [existingFilePreviewUrl, setExistingFilePreviewUrl] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Générer le preview URL pour images et PDF
  useEffect(() => {
    if (!value || !showPreview) {
      setPreviewUrl(null);
      return;
    }

    if (value.type.startsWith('image/')) {
      setFileType('image');
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (value.type === 'application/pdf') {
      setFileType('pdf');
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setFileType('other');
    }
  }, [value, showPreview]);

  // Fetch et générer le preview pour les fichiers existants
  useEffect(() => {
    if (!existingFileUrl || !showPreview || value) {
      setExistingFilePreviewUrl(null);
      return;
    }

    const fetchExistingFile = async () => {
      try {
        const response = await fetch(existingFileUrl);
        if (!response.ok) {
          console.error('Erreur lors du chargement du document existant');
          return;
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setExistingFilePreviewUrl(url);

        if (blob.type.startsWith('image/')) {
          setFileType('image');
        } else if (blob.type === 'application/pdf') {
          setFileType('pdf');
        } else {
          setFileType('other');
        }
      } catch (error) {
        console.error('Erreur lors du fetch du document existant:', error);
      }
    };

    fetchExistingFile();

    return () => {
      if (existingFilePreviewUrl) {
        URL.revokeObjectURL(existingFilePreviewUrl);
      }
    };
  }, [existingFileUrl, showPreview, value]);

  const validateFile = (file: File): string | null => {
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `Le fichier dépasse la taille maximale de ${maxSize} Mo`;
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file) {
      const fileError = validateFile(file);
      if (fileError) {
        setValidationError(fileError);
        return;
      }
    }

    setValidationError(null);
    onChange(file);
  };

  const handleRemove = () => {
    onChange(null);
  };

  const handleView = () => {
    if (value) {
      const url = URL.createObjectURL(value);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } else if (existingFileUrl) {
      window.open(existingFileUrl, '_blank');
    }
  };

  const handleDownload = () => {
    if (value) {
      const url = URL.createObjectURL(value);
      const link = document.createElement('a');
      link.href = url;
      link.download = value.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (existingFileUrl) {
      const link = document.createElement('a');
      link.href = existingFileUrl;
      link.download = existingFileName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileError = validateFile(droppedFile);
      if (fileError) {
        setValidationError(fileError);
        return;
      }
      setValidationError(null);
      onChange(droppedFile);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
  };

  const getDefaultDescription = (): string => {
    if (description) return description;
    return `Max ${maxSize} Mo • ${accept.replace(/\./g, '').toUpperCase()}`;
  };

  return (
    <div className={className}>
      {label && (
        <label className="tw-block tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-700">
          {label} {required && <span className="tw-text-red-500">*</span>}
        </label>
      )}

      {/* Mode preview activé avec fichier uploadé OU fichier existant */}
      {(value || (existingFilePreviewUrl && existingFileUrl)) && showPreview ? (
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-shadow-sm">
          <div className="tw-flex tw-gap-4">
            {/* Vignette de preview */}
            <div className="tw-flex-shrink-0 tw-w-24 tw-h-24 tw-bg-gray-100 tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-center">
              {fileType === 'image' && (previewUrl || existingFilePreviewUrl) && (
                <img
                  src={previewUrl || existingFilePreviewUrl || ''}
                  alt={value?.name || existingFileName}
                  className="tw-w-full tw-h-full tw-object-cover"
                />
              )}

              {fileType === 'pdf' && (previewUrl || existingFilePreviewUrl) && !pdfError && (
                <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-red-50">
                  <Document
                    file={previewUrl || existingFilePreviewUrl || ''}
                    onLoadError={() => setPdfError(true)}
                    className="tw-flex tw-items-center tw-justify-center"
                  >
                    <Page
                      pageNumber={1}
                      width={96}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>
              )}

              {(fileType === 'other' || pdfError || (fileType === 'pdf' && !previewUrl && !existingFilePreviewUrl)) && (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-2">
                  <FileText className="tw-w-10 tw-h-10 tw-text-gray-400 tw-mb-1" />
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500">
                    {value ? getFileExtension(value.name) : getFileExtension(existingFileName)}
                  </span>
                </div>
              )}
            </div>

            {/* Informations et actions */}
            <div className="tw-flex-1 tw-min-w-0">
              <h4 className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate tw-mb-1">
                {value ? value.name : existingFileName}
              </h4>
              <p className="tw-text-xs tw-text-gray-500 tw-mb-3">
                {value ? `${formatFileSize(value.size)} • ${getFileExtension(value.name)}` : existingFileName}
              </p>

              {/* Boutons d'actions */}
              {showActions && (
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  <button
                    type="button"
                    onClick={handleView}
                    disabled={disabled}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-blue-700 tw-bg-affinicar-lighter tw-rounded-md hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  >
                    <Eye className="tw-w-3.5 tw-h-3.5" />
                    Voir
                  </button>

                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={disabled}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-green-700 tw-bg-green-50 tw-rounded-md hover:tw-bg-green-100 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  >
                    <Download className="tw-w-3.5 tw-h-3.5" />
                    Télécharger
                  </button>

                  <button
                    type="button"
                    onClick={value ? handleRemove : () => inputRef.current?.click()}
                    disabled={disabled}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-red-700 tw-bg-red-50 tw-rounded-md hover:tw-bg-red-100 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  >
                    <Trash2 className="tw-w-3.5 tw-h-3.5" />
                    {value ? 'Supprimer' : 'Remplacer'}
                  </button>
                  {/* Input caché pour le remplacement de fichier existant */}
                  {!value && (
                    <input
                      ref={inputRef}
                      type="file"
                      accept={accept}
                      onChange={handleFileChange}
                      disabled={disabled}
                      className="tw-hidden"
                    />
                  )}
                </div>
              )}

              {/* Bouton supprimer minimal si actions désactivées */}
              {!showActions && (
                <button
                  type="button"
                  onClick={value ? handleRemove : () => inputRef.current?.click()}
                  disabled={disabled}
                  className="tw-text-sm tw-text-red-600 hover:tw-text-red-800 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  {value ? 'Supprimer' : 'Remplacer'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : value && !showPreview ? (
        /* Mode simple (rétro-compatible) avec fichier uploadé */
        <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-3">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FileText className="tw-w-4 tw-h-4 tw-text-green-600" />
              <span className="tw-text-sm tw-text-green-700">{value.name}</span>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="tw-text-sm tw-text-red-600 hover:tw-text-red-800 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              title="Retirer le fichier"
            >
              <Trash2 className="tw-w-4 tw-h-4" />
            </button>
          </div>
        </div>
      ) : existingFileUrl ? (
        /* Document existant (pas de nouveau fichier uploadé) */
        <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-3">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FileText className="tw-w-4 tw-h-4 tw-text-blue-600" />
              <span className="tw-text-sm tw-text-blue-700 tw-font-medium">{existingFileName}</span>
            </div>
            <a
              href={existingFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-text-blue-600 hover:tw-text-blue-800 tw-transition-colors"
              title="Télécharger le document actuel"
            >
              <Download className="tw-w-4 tw-h-4" />
            </a>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-blue-700 tw-bg-white tw-border tw-border-blue-300 tw-rounded-md hover:tw-bg-blue-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            Remplacer par un nouveau fichier
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            disabled={disabled}
            data-testid={dataTestId}
            className="tw-hidden"
          />
        </div>
      ) : (
        /* Zone de drop (pas de fichier) */
        <div
          className={`tw-bg-gray-50 tw-border-2 tw-border-dashed tw-rounded-lg tw-p-6 tw-transition-all ${
            disabled
              ? 'tw-opacity-50 tw-cursor-not-allowed'
              : 'tw-cursor-pointer'
          } ${
            isDragging
              ? 'tw-border-blue-500 tw-bg-affinicar-lighter'
              : 'tw-border-gray-300 hover:tw-border-gray-400'
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            required={required}
            disabled={disabled}
            data-testid={dataTestId}
            className="tw-hidden"
          />

          <div className="tw-text-center">
            <Upload className={`tw-w-10 tw-h-10 tw-mx-auto tw-mb-3 ${isDragging ? 'tw-text-affinicar-primary' : 'tw-text-gray-400'}`} />
            <p className="tw-text-sm tw-text-gray-600 tw-mb-1 tw-font-medium">
              {isDragging ? 'Déposez le fichier ici' : 'Glissez-déposez ou cliquez pour parcourir'}
            </p>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">{getDefaultDescription()}</p>
          </div>
        </div>
      )}

      {(error || validationError) && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error || validationError}</p>
      )}
    </div>
  );
};
